import { type RatingsSection } from 'app/components/rating-card';
import { ShowErrors } from 'app/components/show-errors';
import { ShareAndroid } from 'app/design/icons/shareAndroid';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Text } from 'app/design/primitives/text';
import { type useIsExpired } from 'app/hooks/use-is-expired';
import { localeAwareFormatDuration } from 'app/lib/date';
import { type useCreateChannel } from 'app/network/chat/channel';
import { useAllListings } from 'app/network/listing';
import { getMyListingsOffersFilters } from 'app/network/listing-query-filters';
import { MAX_RADIUS_IN_METERS } from 'app/network/preferences';
import { type Profile } from 'app/network/profile';
import { type useMyPlaces } from 'app/network/sharing-place';
import { type Statistics } from 'app/network/statistics';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { ListingType, type Listing } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { isMobileNative, usePageTitle } from 'app/utils/web-helpers';
import type { TFunction } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { Linking, Platform, Share } from 'react-native';
import { VALID_AMBASSADOR_STATUSES } from 'app/hooks/use-ambassador';
import { Event, track } from 'app/lib/analytics';
import { getNextOpenDayKey, type getTodayOpeningStatus } from 'app/lib/opening-hours';
import { WhiteLabel } from 'app/types/white-labels';
import { FeatureFlag, getShareUserProfileUrl, isWhiteLabel } from 'app/utils/helpers';
import * as Clipboard from 'expo-clipboard';
import Toast2 from 'react-native-root-toast';
import ShareSheet from 'react-native-share';
import { IconDots, IconMessages } from '@tabler/icons-react-native';
import { ProfileHeaderHelp } from './profile-header-help';
import { EmojiStat } from './emoji-stat';
import { theme } from './theme';

export { theme } from './theme';

export const getUniqueListingsFromPages = (pages: { results: Listing[] }[] | undefined) => {
  const seenIds = new Set<string>();
  const uniqueListings: Listing[] = [];
  for (const page of pages ?? []) {
    for (const listing of page.results) {
      if (seenIds.has(listing.id)) continue;
      seenIds.add(listing.id);
      uniqueListings.push(listing);
    }
  }
  return uniqueListings;
};

export const ACTIVITY_SECTOR_FEATURE = FeatureFlag.useActivitySector;

export type ProfileNamespace = 'my-profile' | 'neighbour-profile';

export type PlaceDetails = NonNullable<Profile['placeDetails']>;

export type ProfileNavigation = React.ComponentProps<NavigationScreen<'neighbour-profile'>>['navigation'];

export type ProfileRoute = React.ComponentProps<NavigationScreen<'neighbour-profile'>>['route'];

export function getNextOpenDay({
  isPlace,
  openingHours,
  openingStatus,
  t,
}: {
  isPlace: boolean;
  openingHours: PlaceDetails['openingHours'] | undefined;
  openingStatus: ReturnType<typeof getTodayOpeningStatus>;
  t: TFunction;
}) {
  if (!isPlace || !openingHours || openingStatus.isOpen) return undefined;
  if (openingStatus.isBeforeOpening && openingStatus.todayHours) return undefined;
  const nextOpenDayKey = getNextOpenDayKey(openingHours);
  return nextOpenDayKey ? t(`days.${nextOpenDayKey}`, { ns: 'neighbour-profile' }) : undefined;
}

export function isUserFrequentingPlace({
  isPlace,
  placeUserId,
  places,
}: {
  isPlace: boolean;
  placeUserId: string;
  places: ReturnType<typeof useMyPlaces>['data'];
}) {
  if (!isPlace) return false;
  return places?.some((place) => place.placeUserId === placeUserId) ?? false;
}

export function getProfileOffersFilters({
  isLoggedIn,
  isMyself,
  isSmallUI,
  search,
  userId,
}: {
  isLoggedIn: boolean;
  isMyself: boolean;
  isSmallUI: boolean;
  search: string;
  userId: string;
}) {
  if (isMyself) {
    return { ...getMyListingsOffersFilters(isLoggedIn, userId, isSmallUI ? 5 : 10), search };
  }
  return {
    userId,
    take: isSmallUI ? 5 : 10,
    type: ListingType.offer,
    searchDistance: MAX_RADIUS_IN_METERS,
    skipOthersRequests: true,
    includeCounts: true,
    search,
  };
}

export function isInitialListingsLoading(query: ReturnType<typeof useAllListings>) {
  return query.data ? false : query.isFetching && !query.isFetchingNextPage;
}

export function getActiveListingsQuery({
  isPlace,
  neighbourTab,
  offersQuery,
  placeAvailableQuery,
  placeRegularsQuery,
  placeTab,
  requestsQuery,
}: {
  isPlace: boolean;
  neighbourTab: 'offers' | 'requests' | 'reviews' | 'places';
  offersQuery: ReturnType<typeof useAllListings>;
  placeAvailableQuery: ReturnType<typeof useAllListings>;
  placeRegularsQuery: ReturnType<typeof useAllListings>;
  placeTab: 'available' | 'regulars' | 'reviews';
  requestsQuery: ReturnType<typeof useAllListings>;
}) {
  if (isPlace) return placeTab === 'available' ? placeAvailableQuery : placeRegularsQuery;
  if (neighbourTab === 'offers') return offersQuery;
  if (neighbourTab === 'requests') return requestsQuery;
  return undefined;
}

export async function sharePlaceProfile(websiteUrl: string | undefined, profile: Profile | undefined) {
  if (!websiteUrl) return;
  const name = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ');
  await Share.share(Platform.OS === 'ios' ? { url: websiteUrl } : { message: websiteUrl, title: name || websiteUrl });
}

export async function shareNeighbourProfile(
  profile: Pick<Profile, 'userId' | 'firstName' | 'lastName'> | undefined,
  t: TFunction,
) {
  if (!profile) return;
  const url = getShareUserProfileUrl(profile.userId);
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ');
  if (isMobileNative) {
    const action = await ShareSheet.open({ title: name || 'Partage.club', url }).catch(() => ({ message: 'cancel' }));
    track(Event.ShareAction, { action: action.message });
    return;
  }
  await Clipboard.setStringAsync(url);
  Toast2.show(t('urlCopied', { ns: 'one-listing' }), { duration: Toast2.durations.SHORT });
  track(Event.ShareAction, { action: 'web.CopyToPasteboard' });
}

export function openPlaceItinerary(placeDetails: PlaceDetails | null | undefined) {
  const rawPlaceAddress = placeDetails?.address;
  if (typeof rawPlaceAddress !== 'string') return;
  const placeAddress = rawPlaceAddress.trim();
  if (!placeAddress) return;
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(placeAddress)}`;
  Linking.openURL(url).catch((error: unknown) => console.error('Error opening map:', error));
}

export function getProfileStats({
  canPublishListings,
  canRateAfterLoans,
  canUseDonations,
  canUseSales,
  canUseServices,
  statistics,
  t,
}: {
  canPublishListings: boolean | null | undefined;
  canRateAfterLoans: boolean;
  canUseDonations: boolean;
  canUseSales: boolean;
  canUseServices: boolean;
  statistics: Statistics | undefined;
  t: TFunction;
}) {
  const stats: (React.ReactElement | null)[] = [];
  if (canRateAfterLoans) {
    stats.push(
      <EmojiStat
        key="trust-points"
        color="#B8ECE0"
        emoji="👍"
        label={t('stats.trustPoints', { ns: 'neighbour-profile' })}
        value={statistics?.loans.trustPoints.toString() ?? '-'}
      />,
      canPublishListings ? (
        <EmojiStat
          key="requests"
          color={theme.colors['surface.brand.secondary-5.subtle']}
          emoji="🛎️"
          label={t('stats.numberOfRequests', { ns: 'neighbour-profile' })}
          value={statistics?.listings.requests.total.toString() ?? '-'}
        />
      ) : null,
    );
  }
  if (canPublishListings) {
    stats.push(
      <EmojiStat
        key="outgoing-loans"
        color={theme.colors['surface.brand.secondary-5.subtle']}
        emoji="🫴"
        label={t('stats.numberOfOutgoingLoans', { ns: 'neighbour-profile' })}
        value={statistics?.loans.outgoing.total.toString() ?? '-'}
      />,
      <EmojiStat
        key="incoming-loans"
        color={theme.colors['surface.brand.secondary-5.subtle']}
        emoji="👋️"
        label={t('stats.numberOfIncomingLoans', { ns: 'neighbour-profile' })}
        value={statistics?.loans.incoming.total.toString() ?? '-'}
      />,
    );
  }
  stats.push(
    <EmojiStat
      key="chat-response-rate"
      color="#B8ECE0"
      emoji="⏱️"
      label={t('stats.chatResponseRate', { ns: 'neighbour-profile' })}
      value={statistics?.chat?.responseRate ? `${statistics.chat.responseRate.toFixed(0)}%` : '-'}
    />,
    <EmojiStat
      key="chat-response-time"
      color="#B8ECE0"
      emoji="💬️"
      label={t('stats.chatResponseTimeAvg', { ns: 'neighbour-profile' })}
      value={
        statistics?.chat?.averageResponseTime
          ? localeAwareFormatDuration(statistics.chat.averageResponseTime, true, true)
          : '-'
      }
    />,
  );
  if (canUseServices) {
    stats.push(
      <EmojiStat
        key="outgoing-service-loans"
        color="#F8CAAA"
        emoji="💪️"
        label={t('stats.numberOfOutgoingServiceLoans', { ns: 'neighbour-profile' })}
        value={statistics?.serviceLoans?.outgoing.total.toString() ?? '-'}
      />,
    );
    if (canPublishListings) {
      stats.push(
        <EmojiStat
          key="incoming-service-loans"
          color="#F8CAAA"
          emoji="🙏"
          label={t('stats.numberOfIncomingServiceLoans', { ns: 'neighbour-profile' })}
          value={statistics?.serviceLoans?.incoming.total.toString() ?? '-'}
        />,
      );
    }
  }
  if (canUseDonations) {
    if (canPublishListings) {
      stats.push(
        <EmojiStat
          key="outgoing-donations"
          color="#F5C2CB"
          emoji="🎁"
          label={t('stats.numberOfOutgoingDonations', { ns: 'neighbour-profile' })}
          value={statistics?.donations?.outgoing.total.toString() ?? '-'}
        />,
      );
    }
    stats.push(
      <EmojiStat
        key="incoming-donations"
        color="#F5C2CB"
        emoji="🎁"
        label={t('stats.numberOfIncomingDonations', { ns: 'neighbour-profile' })}
        value={statistics?.donations?.incoming.total.toString() ?? '-'}
      />,
    );
  }
  if (canUseSales && canPublishListings) {
    stats.push(
      <EmojiStat
        key="sales"
        color="#F8CAAA"
        emoji="💵"
        label={t('stats.numberOfSales', { ns: 'neighbour-profile' })}
        value={statistics?.sales?.total.toString() ?? '-'}
      />,
    );
  }
  return stats;
}

export function getProfileAccessory({
  canReceiveInAppContact,
  channelMutationError,
  contactButtonHandler,
  isContactPending,
  isMyself,
  isPlace,
  keyboardShown,
  ns,
  onShareProfile,
  paddingHorizontal,
  t,
}: {
  canReceiveInAppContact: boolean;
  channelMutationError: React.ComponentProps<typeof ShowErrors>['error'];
  contactButtonHandler: () => void;
  isContactPending: boolean;
  isMyself: boolean;
  isPlace: boolean;
  keyboardShown: boolean;
  ns: ProfileNamespace;
  onShareProfile: () => void;
  paddingHorizontal: number;
  t: TFunction;
}) {
  if (isMyself || keyboardShown) return false;
  return (
    <Accessory
      style={{
        paddingHorizontal,
        marginTop: theme.spacing['spacing.general.x2'],
      }}
    >
      <Column
        style={
          isMobileNative
            ? {
                shadowColor: 'rgba(0, 0, 0, 1)',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.4,
                shadowRadius: 5,
                elevation: 4,
              }
            : {}
        }
      >
        {canReceiveInAppContact ? (
          <Button
            authorize
            justifyContent="center"
            loading={isContactPending}
            minHeight={48}
            onPress={contactButtonHandler}
            style={{ borderRadius: theme.borderRadii['radius.xl'] }}
            variant="primary"
          >
            <IconMessages color="white" size={20} style={{ marginRight: theme.spacing['spacing.general.x2'] }} />
            <Text color="text.inverse.primary" fontWeight="700" textAlign="center" variant="bodyBase">
              {t(`contactButtonText${isPlace ? 'Place' : 'Member'}`, { ns })}
            </Text>
          </Button>
        ) : null}
        {isPlace ? (
          <Button
            justifyContent="center"
            minHeight={48}
            onPress={onShareProfile}
            style={{
              borderRadius: theme.borderRadii['radius.xl'],
              borderWidth: 1.5,
              borderColor: theme.colors['surface.disabled'],
              marginTop: canReceiveInAppContact ? theme.spacing['spacing.general.x2'] : 0,
            }}
          >
            <Row alignItems="center" gap="spacing.general.x2">
              <ShareAndroid color={theme.colors['text.primary']} width={20} height={20} />
              <Text fontWeight="700" textAlign="center" variant="bodyBase">
                {t('shareProfile', { ns: 'neighbour-profile' })}
              </Text>
            </Row>
          </Button>
        ) : null}
      </Column>
      <ShowErrors error={channelMutationError} />
    </Accessory>
  );
}

export type PlaceTab = 'available' | 'regulars' | 'reviews';

export type NeighbourTab = 'offers' | 'requests' | 'reviews' | 'places';

export type ListingsFilters = Parameters<typeof useAllListings>[0];

export type ListingsQuery = ReturnType<typeof useAllListings>;

export type Rating = React.ComponentProps<typeof RatingsSection>['ratings'][number];

export interface ProfileListingResults {
  filters: ListingsFilters;
  isLoading: boolean;
  listings: Listing[];
  query: ListingsQuery;
  total: number;
}

export function navigateToRatingEditor(navigation: ProfileNavigation, rating: Rating) {
  navigation.navigate(
    'update-rating',
    {
      loanId: rating.loanId,
      listingId: rating.listingId,
      channelUrl: rating.channelUrl,
      isLender: rating.reviewerRole === 'lender',
      subjectName:
        [rating.ratedUserFirstName, rating.ratedUserLastName].filter(Boolean).join(' ').trim() ||
        rating.listingTitle ||
        '',
      subjectImage: null,
      overallRating: rating.overallRating,
      assetQualityRating: rating.assetQualityRating,
      logisticsReliabilityRating: rating.logisticsReliabilityRating,
      communityTrustRating: rating.communityTrustRating,
      comment: rating.comment,
      initialExperience: (rating.overallRating ?? 0) >= 3 ? 'positive' : 'negative',
    },
    { pop: true },
  );
}

export function getOpeningStatusDetails({
  nextOpenDay,
  openingStatus,
  t,
}: {
  nextOpenDay: string | undefined;
  openingStatus: ReturnType<typeof getTodayOpeningStatus>;
  t: TFunction;
}) {
  if (openingStatus.isOpen && openingStatus.closingTime) {
    return t('place.until', { time: openingStatus.closingTime, ns: 'neighbour-profile' });
  }
  if (!openingStatus.isOpen && openingStatus.isBeforeOpening && openingStatus.todayHours) {
    return openingStatus.todayHours;
  }
  if (nextOpenDay) return t('place.closedUntil', { day: nextOpenDay, ns: 'neighbour-profile' });
  return '';
}

export function getAllProfileListingFilters({
  isLoggedIn,
  isMyself,
  isSmallUI,
  search,
  userId,
}: {
  isLoggedIn: boolean;
  isMyself: boolean;
  isSmallUI: boolean;
  search: string;
  userId: string;
}) {
  const smallPageSize = isSmallUI ? 5 : undefined;
  return {
    offers: getProfileOffersFilters({ isLoggedIn, isMyself, isSmallUI, search, userId }),
    placeAvailable: { userId, type: ListingType.offer, search, take: smallPageSize },
    placeRegulars: { sharingPlaceUserId: userId, type: ListingType.offer, search, take: smallPageSize },
    requests: { userId, take: smallPageSize, type: ListingType.request, includeCounts: true, search },
  };
}

export function getProfileListingResults(
  filters: ListingsFilters,
  query: ListingsQuery,
  isLoading = isInitialListingsLoading(query),
): ProfileListingResults {
  return {
    filters,
    isLoading,
    listings: getUniqueListingsFromPages(query.data?.pages),
    query,
    total: query.data?.pages[0]?.total ?? 0,
  };
}

export function useActiveListingsPagination({
  activeListingsQuery,
  isPlace,
  neighbourTab,
  placeTab,
  searchQuery,
  userId,
}: {
  activeListingsQuery: ListingsQuery | undefined;
  isPlace: boolean;
  neighbourTab: NeighbourTab;
  placeTab: PlaceTab;
  searchQuery: string;
  userId: string;
}) {
  const hasDraggedPageRef = useRef(false);
  const fetchNextActivePage = () => {
    if (!activeListingsQuery?.hasNextPage || activeListingsQuery.isFetchingNextPage) return;
    if (isMobileNative) hasDraggedPageRef.current = false;
    void activeListingsQuery.fetchNextPage();
  };
  useEffect(() => {
    hasDraggedPageRef.current = false;
  }, [isPlace, searchQuery, neighbourTab, placeTab, userId]);
  const handlePageScrollBeginDrag = () => {
    hasDraggedPageRef.current = true;
  };
  const handlePageScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!activeListingsQuery || (isMobileNative && !hasDraggedPageRef.current)) return;
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
    if (distanceFromBottom <= 300) fetchNextActivePage();
  };
  return { handlePageScroll, handlePageScrollBeginDrag };
}

export function useProfileListingsData({
  profileTab = 'listings',
  isLoggedIn,
  isMyself,
  isPlace,
  isSmallUI,
  userId,
}: {
  profileTab?: 'overview' | 'listings' | 'reviews' | 'places';
  isLoggedIn: boolean;
  isMyself: boolean;
  isPlace: boolean;
  isSmallUI: boolean;
  userId: string;
}) {
  const [placeTab, setPlaceTab] = useState<PlaceTab>('available');
  const [neighbourTab, setNeighbourTab] = useState<NeighbourTab>('offers');
  const [searchQuery, setSearchQuery] = useState('');
  const filters = getAllProfileListingFilters({
    isLoggedIn,
    isMyself,
    isSmallUI,
    search: searchQuery.trim(),
    userId,
  });
  const placeAvailableQuery = useAllListings(filters.placeAvailable, 'NeighbourProfileScreen-PlaceAvailable', {
    enabled: isPlace && placeTab === 'available',
    throwOnError: true,
  });
  const placeRegularsQuery = useAllListings(filters.placeRegulars, 'NeighbourProfileScreen-PlaceRegulars', {
    enabled: isPlace && placeTab === 'regulars',
    throwOnError: true,
  });
  const offersQuery = useAllListings(filters.offers, 'NeighbourProfileScreen-Offers', {
    enabled: !isPlace && (profileTab === 'overview' || (profileTab === 'listings' && neighbourTab === 'offers')),
    throwOnError: true,
  });
  const requestsQuery = useAllListings(filters.requests, 'NeighbourProfileScreen-Requests', {
    enabled: !isPlace && (profileTab === 'overview' || (profileTab === 'listings' && neighbourTab === 'requests')),
    throwOnError: true,
  });
  const activeListingsQuery = getActiveListingsQuery({
    isPlace,
    neighbourTab,
    offersQuery,
    placeAvailableQuery,
    placeRegularsQuery,
    placeTab,
    requestsQuery,
  });
  const pagination = useActiveListingsPagination({
    activeListingsQuery: !isPlace && profileTab !== 'listings' ? undefined : activeListingsQuery,
    isPlace,
    neighbourTab,
    placeTab,
    searchQuery,
    userId,
  });
  return {
    neighbourTab,
    offers: getProfileListingResults(filters.offers, offersQuery),
    pagination,
    placeAvailable: getProfileListingResults(filters.placeAvailable, placeAvailableQuery),
    placeRegulars: getProfileListingResults(
      filters.placeRegulars,
      placeRegularsQuery,
      placeRegularsQuery.isFetching && !placeRegularsQuery.isFetchingNextPage,
    ),
    placeTab,
    requests: getProfileListingResults(filters.requests, requestsQuery),
    searchQuery,
    setNeighbourTab,
    setPlaceTab,
    setSearchQuery,
  };
}

export function isViewingOwnProfile(currentUserId: string | undefined, profileUserId: string) {
  return Boolean(currentUserId && currentUserId === profileUserId);
}

export function hasPlaceDetails(placeDetails: PlaceDetails | null | undefined): placeDetails is PlaceDetails {
  return placeDetails !== null && placeDetails !== undefined;
}

export function shouldShowSuperSharer(profile: Profile | undefined) {
  return Boolean(profile?.isSuper) && !isWhiteLabel(WhiteLabel.HauteGaspesie);
}

export function isViewedAmbassador(ambassadorEnabled: boolean, status: Profile['ambassadorStatus']) {
  return Boolean(ambassadorEnabled && status && VALID_AMBASSADOR_STATUSES.includes(status));
}

export function getProfileNavigationOptions({
  isLargeUI,
  isPlace,
  isRoot,
  ns,
  onMore,
  onHelp,
  t,
}: {
  isLargeUI: boolean;
  isPlace: boolean;
  isRoot: boolean;
  ns: ProfileNamespace;
  onMore: () => void;
  onHelp: () => void;
  t: TFunction;
}) {
  const suffix = isPlace ? 'SharingPlace' : '';
  return {
    ...(isRoot ? { headerBackVisible: false } : {}),
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {t(`title${suffix}`, { ns })}
      </Text>
    ),
    headerRight: isPlace
      ? () => <ProfileHeaderHelp isLargeUI={isLargeUI} onPress={onHelp} />
      : () => (
          <Button
            accessibilityLabel={t('profile.moreActions', { ns: 'accessibility' })}
            onPress={onMore}
            variant="plain"
          >
            <IconDots color={theme.colors['text.primary']} size={24} />
          </Button>
        ),
  };
}

export function useProfilePageTitle({
  isPlace,
  ns,
  profile,
  t,
}: {
  isPlace: boolean;
  ns: ProfileNamespace;
  profile: Profile | undefined;
  t: TFunction;
}) {
  const setPageTitle = usePageTitle();
  const suffix = isPlace ? 'SharingPlace' : '';
  useEffect(() => {
    if (!profile) return;
    setPageTitle(`${t(`title${suffix}`, { ns })} - {{name}}`, {
      name: [profile.firstName, profile.lastName].filter(Boolean).join(' '),
    });
  }, [ns, profile, setPageTitle, suffix, t]);
}

export function contactProfile({
  authorize,
  mutateChannel,
  navigation,
  profile,
  route,
}: {
  authorize: ReturnType<typeof useIsExpired>;
  mutateChannel: ReturnType<typeof useCreateChannel>['mutate'];
  navigation: ProfileNavigation;
  profile: Profile | undefined;
  route: ProfileRoute;
}) {
  if (!profile) return;
  if (route.params.channelURL) {
    navigateToChat(navigation, route.params.channelURL);
    return;
  }
  if (route.params.listingType === ListingType.offer) {
    const listingId = route.params.listingId;
    if (!listingId) {
      mutateChannel({ id: profile.userId });
      return;
    }
    authorize(() => mutateChannel({ id: listingId }));
    return;
  }
  mutateChannel({ id: route.params.listingId ?? profile.userId });
}
